import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Loader2 } from "lucide-react";
import Logo from "./Logo";
import { contact, footer, navLinks } from "../mock";

const API = "/api";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", event: "", requirements: "", website: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/briefs.php`, form);
      toast.success("Thank you! Your brief has been received. We'll be in touch shortly.");
      setForm({ name: "", email: "", event: "", requirements: "", website: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-charcoal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition";
  const labelCls = "block text-charcoal text-sm font-medium mb-2";

  return (
    <section id="contact" className="section-pad bg-white">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <p className="eyebrow mb-4">CONTACT US</p>
            <h2 className="heading-2 mb-5">Planning Your Next Exhibition?</h2>
            <p className="text-body text-lg mb-8">
              Share your exhibition details, stand size and requirements, and we'll discuss a suitable design and budget.
            </p>
            <div className="space-y-4">
              <a href={`mailto:${contact.email}`} className="flex items-center gap-3 text-charcoal hover:text-brand transition-colors">
                <span className="w-10 h-10 rounded-lg bg-brand-soft flex items-center justify-center"><Mail size={18} className="text-brand" /></span>
                {contact.email}
              </a>
              <a href={`tel:${contact.phone}`} className="flex items-center gap-3 text-charcoal hover:text-brand transition-colors">
                <span className="w-10 h-10 rounded-lg bg-brand-soft flex items-center justify-center"><Phone size={18} className="text-brand" /></span>
                {contact.phone}
              </a>
            </div>
          </div>

          <div className="lg:col-span-3 bg-soft border border-gray-200 rounded-2xl p-7 lg:p-9">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div style={{display: "none"}} aria-hidden="true"><input name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} /></div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Your Name</label>
                  <input className={field} required maxLength={200} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Email Address</label>
                  <input className={field} required type="email" maxLength={254} placeholder="you@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Exhibition / Event Name</label>
                <input className={field} maxLength={300} placeholder="e.g. India International Trade Fair, Plastindia" value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Stand Size & Requirements</label>
                <textarea rows={4} maxLength={10000} className={field} placeholder="Stand size, open sides, key requirements, approximate budget..." value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-70">
                {loading ? (<><Loader2 size={18} className="animate-spin" /> Sending...</>) : "Send Your Brief"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#1b1f27] text-gray-300">
      <div className="rainbow-bar h-1 w-full" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <Logo onDark className="h-10" />
          <p className="text-gray-400 leading-relaxed mt-5 text-sm max-w-xs">{footer.description}</p>
        </div>
        <div>
          <h4 className="font-display font-semibold text-white tracking-wider text-sm mb-5">SERVICES</h4>
          <ul className="space-y-3 text-sm">
            {footer.services.map((s, i) => (
              <li key={i}><button onClick={() => scrollTo("services")} className="text-gray-400 hover:text-white transition-colors">{s}</button></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-display font-semibold text-white tracking-wider text-sm mb-5">QUICK LINKS</h4>
          <ul className="space-y-3 text-sm">
            {footer.quickLinks.map((q, i) => {
              const match = navLinks.find((n) => n.label === q);
              return (
                <li key={i}><button onClick={() => scrollTo(match ? match.href.slice(1) : "home")} className="text-gray-400 hover:text-white transition-colors">{q}</button></li>
              );
            })}
          </ul>
        </div>
        <div>
          <h4 className="font-display font-semibold text-white tracking-wider text-sm mb-5">CONTACT</h4>
          <ul className="space-y-4 text-sm">
            <li className="flex items-center gap-3"><Mail size={18} className="text-brand" /> <a href={`mailto:${contact.email}`} className="text-gray-400 hover:text-white">{contact.email}</a></li>
            <li className="flex items-center gap-3"><Phone size={18} className="text-brand" /> <a href={`tel:${contact.phone}`} className="text-gray-400 hover:text-white">{contact.phone}</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-gray-500">
          <p>© 2026 Rainbow Worldwide. All rights reserved.</p>
          <p>Exhibition Stand Design &amp; Construction — India</p>
        </div>
      </div>
    </footer>
  );
}
